(ns io.daskul.tutorial.webapp.domain.conversations.events
  "Re-frame events for conversation thread — reply and message polling."
  (:require
   [re-frame.core :as rf]
   [io.daskul.webapp.aggregates.events :as agg.events]
   [io.daskul.webapp.widgets.conversation.linked-leads :as ll]))

(def msg-params
  "Standard params for listing messages in a conversation. No :sort — the
   server's default (sent-at asc, id tiebreak; SMS-243) is the single source
   of truth for thread order."
  {:page-size 100})

;; ============================================================================
;; Reply
;; ============================================================================

(rf/reg-event-fx ::send-reply
  (fn [{:keys [db]} [_ conversation-id text]]
    {:db (assoc-in db [:conversations :sending?] true)
     :http-xhrio (agg.events/build-request
                  {:method :post
                   :path (str "/conversations/" conversation-id "/messages")
                   :params {:text text}
                   :on-success [::send-reply-success conversation-id]
                   :on-failure [::send-reply-failure]})}))

(rf/reg-event-fx ::send-reply-success
  (fn [{:keys [db]} [_ conversation-id _response]]
    {:db (assoc-in db [:conversations :sending?] false)
     :dispatch-n [[::agg.events/list :message
                   (assoc msg-params :conversation-id conversation-id)]
                  [::agg.events/get :conversation conversation-id]]}))

(rf/reg-event-fx ::send-reply-failure
  (fn [{:keys [db]} [_ _error]]
    {:db (assoc-in db [:conversations :sending?] false)}))

;; ============================================================================
;; Resend
;; ============================================================================

(rf/reg-event-fx ::resend-message
  (fn [{:keys [db]} [_ conversation-id message-id]]
    {:db (assoc-in db [:conversations :resending message-id] true)
     :http-xhrio (agg.events/build-request
                  {:method :post
                   :path (str "/conversations/" conversation-id
                              "/messages/" message-id "/resend")
                   :on-success [::resend-message-success conversation-id message-id]
                   :on-failure [::resend-message-failure message-id]})}))

(rf/reg-event-fx ::resend-message-success
  (fn [{:keys [db]} [_ conversation-id message-id _response]]
    {:db (update-in db [:conversations :resending] dissoc message-id)
     :dispatch [::agg.events/list :message
                (assoc msg-params :conversation-id conversation-id)]}))

(rf/reg-event-fx ::resend-message-failure
  (fn [{:keys [db]} [_ message-id _error]]
    {:db (update-in db [:conversations :resending] dissoc message-id)}))

;; ============================================================================
;; Mark as read (SMS-29)
;; ============================================================================

(rf/reg-event-fx ::mark-as-read
  (fn [_ [_ conversation-id]]
    {:http-xhrio (agg.events/build-request
                  {:method :post
                   :path (str "/conversations/" conversation-id "/read")
                   :on-success [::mark-as-read-success]
                   :on-failure [::mark-as-read-failure]})}))

(rf/reg-event-db ::mark-as-read-success
  (fn [db _] db))

(rf/reg-event-db ::mark-as-read-failure
  (fn [db _] db))

;; ============================================================================
;; Linked Leads
;; ============================================================================

(rf/reg-event-fx ::fetch-linked-leads
  (fn [{:keys [db]} [_ conversation-id]]
    {:db (assoc-in db [:conversations :leads-loading?] true)
     :http-xhrio (agg.events/build-request
                  {:method :get
                   :path (str "/leads?conversation-id=" conversation-id)
                   :on-success [::fetch-linked-leads-success conversation-id]
                   :on-failure [::fetch-linked-leads-failure]})}))

(rf/reg-event-db ::fetch-linked-leads-success
  (fn [db [_ conversation-id response]]
    ;; Stored WITH the conversation it was fetched for (#760): one global slot
    ;; plus a header chip that names a lead is how thread A's lead ends up
    ;; labelling thread B while B's own fetch is still in flight. ::linked-leads
    ;; hands the match check to ll/for-conversation.
    (-> db
        (assoc-in [:conversations :linked-leads] {:conversation-id conversation-id
                                                  :items (:items response)})
        (assoc-in [:conversations :leads-loading?] false))))

(rf/reg-event-db ::fetch-linked-leads-failure
  (fn [db [_ _error]]
    (assoc-in db [:conversations :leads-loading?] false)))

;; ============================================================================
;; Suggested links — "is this someone we already know?" (#767)
;; ============================================================================

(rf/reg-event-fx ::fetch-suggested-links
  (fn [_ [_ conversation-id]]
    {:http-xhrio (agg.events/build-request
                  {:method :get
                   :path (str "/conversations/" conversation-id "/suggested-links")
                   :on-success [::fetch-suggested-links-success conversation-id]
                   :on-failure [::fetch-suggested-links-failure]})}))

(rf/reg-event-db ::fetch-suggested-links-success
  (fn [db [_ conversation-id response]]
    ;; Stamped with the conversation it was fetched for, same as linked-leads
    ;; (#760): one global slot plus a panel that names people is how thread A's
    ;; suggestions end up offered on thread B while B's own fetch is in flight.
    (assoc-in db [:conversations :suggested-links]
              {:conversation-id conversation-id :payload response})))

(rf/reg-event-db ::fetch-suggested-links-failure
  (fn [db _]
    ;; Suggestions are an offer, not the point of the screen. A failed fetch
    ;; shows no panel and says nothing — the admin still has Link existing.
    (update db :conversations dissoc :suggested-links)))

;; ============================================================================
;; Link an existing lead (#767)
;; ============================================================================

;; Open state lives here rather than in the view's use-state because the thing
;; that closes this slide-over is a SERVER round-trip, not a click: the picker
;; has no on-created callback to hang a close on, so ::link-lead-success is what
;; dismisses it. Create Lead keeps component state — lookup-inline-create hands
;; it an :on-created, so the close stays local there.
(rf/reg-event-db ::open-link-lead
  (fn [db _]
    (-> db
        (assoc-in [:conversations :link-lead-open?] true)
        (update :conversations dissoc :link-lead-error))))

(rf/reg-event-db ::close-link-lead
  (fn [db _]
    (-> db
        (assoc-in [:conversations :link-lead-open?] false)
        (update :conversations dissoc :link-lead-error))))

(rf/reg-event-fx ::link-lead
  (fn [{:keys [db]} [_ conversation-id lead-id]]
    {:db (-> db
             (assoc-in [:conversations :linking-lead?] true)
             (update :conversations dissoc :link-lead-error))
     :http-xhrio (agg.events/build-request
                  {:method :post
                   :path (str "/conversations/" conversation-id "/link-lead")
                   :params {:lead-id lead-id}
                   :on-success [::link-lead-success conversation-id]
                   :on-failure [::link-lead-failure]})}))

(rf/reg-event-fx ::link-lead-success
  (fn [{:keys [db]} [_ conversation-id _response]]
    {:db (-> db
             (assoc-in [:conversations :linking-lead?] false)
             (assoc-in [:conversations :link-lead-open?] false)
             (update :conversations dissoc :link-lead-error))
     ;; Both, not just the leads: linking runs the lead's after-write hooks
     ;; server-side, so the conversation may have just gained a branch (T-015)
     ;; or moved new → linked. Re-reading only the leads would leave the aside's
     ;; status panel showing the state from before the action the admin just took.
     ;; …and re-ask for suggestions: the thread now HAS a lead, so the server's
     ;; suppression rule says there are none, and the panel disappears on its
     ;; own rather than needing the view to guess.
     :dispatch-n [[::fetch-linked-leads conversation-id]
                  [::fetch-suggested-links conversation-id]
                  [::agg.events/get :conversation conversation-id]]}))

(rf/reg-event-db ::link-lead-failure
  (fn [db [_ error]]
    ;; The server's own message, verbatim — the one case that matters here is
    ;; 409 lead-already-linked, and its message says what to do about it. A
    ;; generic "Failed to link" would throw that away.
    (-> db
        (assoc-in [:conversations :linking-lead?] false)
        (assoc-in [:conversations :link-lead-error]
                  (or (get-in error [:response :message])
                      "Could not link that lead. Please try again.")))))

(rf/reg-event-db ::clear-link-lead-error
  (fn [db _]
    (update db :conversations dissoc :link-lead-error)))


;; ============================================================================
;; Subscriptions
;; ============================================================================

(rf/reg-sub ::sending?
  (fn [db _]
    (get-in db [:conversations :sending?] false)))

(rf/reg-sub ::resending?
  (fn [db [_ message-id]]
    (get-in db [:conversations :resending message-id] false)))

(rf/reg-sub ::linked-leads
  (fn [db [_ conversation-id]]
    (ll/for-conversation (get-in db [:conversations :linked-leads]) conversation-id)))

(rf/reg-sub ::leads-loading?
  (fn [db _]
    (get-in db [:conversations :leads-loading?] false)))

(rf/reg-sub ::linking-lead?
  (fn [db _]
    (get-in db [:conversations :linking-lead?] false)))

(rf/reg-sub ::link-lead-error
  (fn [db _]
    (get-in db [:conversations :link-lead-error])))

(rf/reg-sub ::link-lead-open?
  (fn [db _]
    (get-in db [:conversations :link-lead-open?] false)))

(rf/reg-sub ::suggested-links
  (fn [db [_ conversation-id]]
    (let [cached (get-in db [:conversations :suggested-links])]
      (when (and (some? conversation-id)
                 (= (:conversation-id cached) conversation-id))
        (:payload cached)))))

